import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "common/components/Button/Button";
import { Input } from "common/components/Input/Input";
import { QueryLayout } from "common/components/Layout/QueryLayout";
import { Error } from "common/components/Message/Error/Error";
import { SubTitle } from "common/components/Title/SubTitle";
import { getErrorMessage } from "common/utils/helpers";
import { IPlatformSettings } from "common/models/settings/IPlatformSettings";
import {
  useGetPlatformSettingsQuery,
  useUpdatePlatformSettingsMutation,
} from "services/admin/adminSettingsApi";

interface IPlatformSettingFormProps {
  // Ключ настройки, которую редактирует форма
  name: keyof IPlatformSettings;
  title: string;
  label: string;
  hint: string;
  placeholder: string;
  min: number;
  max: number;
  // Шаг ввода: 1 для целых значений (часы), 0.1 для процентов
  step: number;
}

// Форма одной числовой настройки платформы: читает все настройки, но
// отправляет только своё поле
export const PlatformSettingForm: FC<IPlatformSettingFormProps> = ({
  name,
  title,
  label,
  hint,
  placeholder,
  min,
  max,
  step,
}) => {
  const {
    data: settings,
    isLoading,
    isFetching,
    error,
  } = useGetPlatformSettingsQuery();

  const [update, { isLoading: isUpdating, error: updateError, isSuccess }] =
    useUpdatePlatformSettingsMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<Partial<IPlatformSettings>>();

  useEffect(() => {
    if (settings) {
      reset({ [name]: settings[name] });
    }
  }, [settings, name, reset]);

  const onSubmit = handleSubmit((data) => {
    update({ [name]: Number(data[name]) });
  });

  const fieldError = errors[name];

  return (
    <QueryLayout isLoading={isLoading} isFetching={isFetching} error={error}>
      <form className="grid max-w-[420px] gap-[15px]" onSubmit={onSubmit}>
        <SubTitle>{title}</SubTitle>
        <Input
          {...register(name, {
            required: "Укажите значение",
            min: { value: min, message: `Не меньше ${min}` },
            max: { value: max, message: `Не больше ${max}` },
            validate: (value) =>
              step !== 1 ||
              Number.isInteger(Number(value)) ||
              "Только целое число",
          })}
          className="w-full"
          label={label}
          type="number"
          step={step}
          min={min}
          max={max}
          invalid={!!fieldError}
          invalidMessage={fieldError?.message}
          placeholder={placeholder}
        />
        <p className="text-xs text-lightGray">{hint}</p>
        {updateError && (
          <Error>{getErrorMessage(updateError)?.data.message}</Error>
        )}
        {isSuccess && !isDirty && (
          <p className="text-sm text-primary">Сохранено</p>
        )}
        <Button className="w-fit" disabled={!isDirty || isUpdating}>
          Сохранить
        </Button>
      </form>
    </QueryLayout>
  );
};
